import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RetryAction, Button, DataTable, ListRow, Loading, Meter, Page, PageHeader, Section, Unavailable, Waiting } from '@beyond-js/ui/react';
import { config } from '@snapshots/config';
import { Links, type Storage } from '@snapshots/domain';
import { Copies, Origin, useFrame, usePlace } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import type { Refusal } from '@snapshots/transport';
import { Failure, Outage, Outages, Problems, Updated } from '@snapshots/ui';
import { Scope } from './columns';

/**
 * What an organization's snapshots hold (SNP-12, decision E9): how many it keeps and how many bytes they hold
 * against its quota, each as the family's `Meter`; what deleted snapshots still hold until they are removed;
 * and the same by project. The organization is the address's (`?organization=`), or the person's only one.
 * It is one read: nobody answering it is the page's one line (D163), its body while nothing was read, and
 * over what was read earlier it says when that was.
 */
export function StorageView() {
	const [parameters] = useSearchParams();
	const { text, captures, identity, language, size, count, relative } = usePresence();
	const { arrival, names, descriptor } = useFrame();
	const organizations = identity?.organizations ?? [];
	const organization = parameters.get('organization') || (organizations.length === 1 ? organizations[0].organization : undefined);
	// What was read, for which organization and when: another organization's numbers are never shown as this one's
	const [held, setHeld] = useState<{ organization: string; storage: Storage; at: Date }>();
	const storage = held && held.organization === organization ? held.storage : undefined;
	// A refusal is the page's own answer; a read nobody answered is its one line
	const [problem, setProblem] = useState<Refusal>();
	const [missed, setMissed] = useState<Refusal>();
	const [attempt, setAttempt] = useState(0);
	usePlace(organization ? { organization } : {});
	useEffect(() => {
		document.title = text('page.storage');
	}, [text]);
	// One read of what the organization holds, resolving whether it was answered
	const load = useCallback(
		async (live: () => boolean = () => true): Promise<boolean> => {
			if (!organization) return true;
			try {
				const found = await captures.storage(organization);
				if (live()) {
					setHeld({ organization, storage: found, at: new Date() });
					setProblem(undefined);
					setMissed(undefined);
				}
				return true;
			} catch (cause) {
				const refusal = Problems.refusal(cause);
				if (!live() || refusal.quiet) return true;
				if (Outage.cause(refusal)) {
					setMissed(refusal);
					return false;
				}
				// A refusal is an answer: it ends the line
				setMissed(undefined);
				setProblem(refusal);
				return true;
			}
		},
		[captures, organization]
	);
	useEffect(() => {
		let live = true;
		setProblem(undefined);
		setMissed(undefined);
		void load(() => live);
		return () => {
			live = false;
		};
	}, [load, attempt]);

	const named = organization ? (names.name(organization) ?? organization) : undefined;
	const reads = missed ? [{ key: 'storage', title: text('outage.read.storage'), refusal: missed, run: () => load() }] : [];
	// The one line keeps one place, at the column's start, whether it is the page's body or a line above it
	const page = (children: ReactNode) => (
		<Page template="list" width="fluid" arrival={<Origin organization={organization} />} header={<PageHeader title={text('storage.title')} headingRef={arrival.heading} lead={named ? text('storage.scope', { organization: named }) : undefined} />}>
			<Outages reads={reads} whole={!storage} text={text} language={language} />
			{children}
		</Page>
	);
	const owner = Copies.unavailable(language);
	if (!organization) {
		if (!organizations.length) {
			if (descriptor === null) return page(<Waiting label={Loading.text({ name: text('loading.organizations'), language })} language={language} kind="lines" />);
			const accounts = new Links(descriptor).entry('accounts');
			const action = accounts?.url && accounts.available
				? <Button variant="primary" href={accounts.url} label={text('unavailable.member.action')} />
				: config.projects ? <Button variant="primary" href={config.projects} label={text('unavailable.member.projects')} /> : undefined;
			return page(<Unavailable kind="association" title={text('unavailable.member.title')} reason={text('unavailable.member.reason')} owner={text('unavailable.member.owner')} labels={owner} action={action} />);
		}
		return page(
			<Section title={text('storage.choose')}>
				<ul className="bui-rows" data-framed data-storage="choose">
					{organizations.map(entry => <ListRow key={entry.organization} title={entry.name ?? text('storage.unnamed')} href={`/storage?${new URLSearchParams({ organization: entry.organization })}`} />)}
				</ul>
			</Section>
		);
	}

	if (problem?.code === 'NOT_FOUND') {
		return page(<Unavailable kind="access" title={text('unavailable.organization.title')} reason={text('unavailable.organization.reason')} owner={text('unavailable.organization.owner')} labels={owner} details={Problems.details(problem)} action={<Button variant="primary" href="/" label={text('unavailable.all')} />} />);
	}
	if (problem) return page(<Failure refusal={problem} text={text} locale={language} attempt="read" actions={<RetryAction labels={{ retry: text('action.retry') }} onClick={() => setAttempt(value => value + 1)} />} />);
	// Nothing read yet and nobody answered: the line is the page's body
	if (missed && !storage) return page(null);
	// The usage and the projects wait in their own places, under the header already drawn (LOAD-01)
	if (!storage) {
		return page(
			<div className="flex flex-col gap-6" data-storage-waiting={organization}>
				<Waiting label={Loading.text({ name: text('loading.storage'), language })} language={language} kind="lines" rows={2} />
				<Section title={text('storage.projects')} waiting={{ language, kind: 'table', rows: 2 }} />
			</div>
		);
	}
	const share = (used: number, quota: number) => (quota > 0 ? used / quota : null);
	return page(
		<div className="flex flex-col gap-6" data-storage={storage.organization}>
			{missed && <Updated time={held?.at} language={language} />}
			<div className="grid gap-4 md:grid-cols-2">
				<div className="flex min-w-0 flex-col gap-2" data-usage="snapshots">
					<Meter label={text('storage.snapshots')} value={share(storage.used.snapshots, storage.quota.snapshots)} locale={language} />
					<p className="text-small" data-used="snapshots">{text('storage.meter.snapshots', { used: storage.used.snapshots, quota: storage.quota.snapshots })}</p>
				</div>
				<div className="flex min-w-0 flex-col gap-2" data-usage="bytes">
					<Meter label={text('storage.bytes')} value={share(storage.used.bytes, storage.quota.bytes)} locale={language} />
					<p className="text-small" data-used="bytes">{text('storage.meter.bytes', { used: size(storage.used.bytes), quota: size(storage.quota.bytes) })}</p>
				</div>
			</div>
			<p className="text-small text-ink-muted">
				{text('storage.same')} {text('storage.checked', { time: relative(storage.checked) })}
			</p>
			{storage.deleted.snapshots > 0 && <p className="text-small">{count('storage.deleted', storage.deleted.snapshots, { bytes: size(storage.deleted.bytes) })}</p>}
			<Projects storage={storage} />
		</div>
	);
}

/**
 * What each project's snapshots hold, to compare them (L1): the family's `DataTable`, its search in the
 * section's heading line (L8), sorted by what is stored by default, a project leading to its list.
 */
function Projects({ storage }: { storage: Storage }) {
	const { text, size, language } = usePresence();
	const { names } = useFrame();
	const [outlet, setOutlet] = useState<HTMLSpanElement | null>(null);
	type Row = Storage['projects'][number];
	const labels = useMemo(() => ({ ...DataTable.labels[language], search: text('storage.search') }), [language, text]);
	const columns = [
		{ key: 'project', label: text('storage.column.project'), primary: true, sort: true, value: (row: Row) => names.name(row.project) ?? '', render: (row: Row) => <Scope project={row.project} /> },
		{ key: 'snapshots', label: text('storage.column.snapshots'), align: 'end' as const, sort: true, value: (row: Row) => row.snapshots },
		{ key: 'bytes', label: text('storage.column.bytes'), align: 'end' as const, sort: true, value: (row: Row) => row.bytes, render: (row: Row) => size(row.bytes) }
	];
	return (
		<Section title={text('storage.projects')} actions={<span ref={setOutlet} data-storage-tools />}>
			<div data-storage-projects>
				<DataTable<Row> label={text('storage.projects')} columns={columns} rows={storage.projects} rowKey={row => row.project} tools="external" toolsTarget={outlet} state={{ sort: 'bytes', direction: 'descending' }} empty={{ title: text('storage.empty') }} labels={labels} />
			</div>
		</Section>
	);
}
